import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { websiteApi } from "../../api/websiteApi";
import "./Services.css";

/* =========================================================
   HERO SLIDES
   ========================================================= */

const SLIDE_DURATION = 5000;

const heroSlides = [
  {
    img: "/images/services/WebsiteDesign1.png",
    badge: "WEB DESIGN & DEVELOPMENT",
    title: "Build a Website That Stands Out",
    desc: "We create modern, responsive, and professional websites designed to help your business grow online.",
  },
  {
    img: "/images/services/WebsiteDesign2.png",
    badge: "DIGITAL SOLUTIONS",
    title: "Your Vision. Our Creative Design.",
    desc: "From stunning visuals to seamless user experiences, we build websites that leave a lasting impression.",
  },
  {
    img: "/images/services/WebsiteDesign3.png",
    badge: "CREATIVE WEB AGENCY",
    title: "Websites Designed for Growth",
    desc: "Transform your online presence with a fast, modern, and responsive website built for your business.",
  },
];

/* =========================================================
   SERVICES
   ========================================================= */

const services = [
  {
    number: "01",
    icon: "fa-laptop-code",
    title: "Website Design & Development",
    desc: "Professional websites designed to establish your brand online and turn visitors into customers.",
    features: ["Responsive Website Design", "Business & Corporate Websites", "Landing Pages", "Website Redesign"],
  },
  {
    number: "02",
    icon: "fa-shopping-cart",
    title: "Online Stores",
    desc: "Powerful e-commerce solutions that allow your business to sell products online and manage orders.",
    features: ["Online Store Development", "Product Management", "Payment Gateway Integration", "Order Management"],
  },
  {
    number: "03",
    icon: "fa-code",
    title: "Custom Web Applications",
    desc: "Custom-built web applications designed around your unique business processes and requirements.",
    features: ["Business Applications", "Client Portals", "Management Systems", "Custom Functionality"],
  },
  {
    number: "04",
    icon: "fa-cloud",
    title: "Hosting & Infrastructure",
    desc: "Reliable hosting and infrastructure solutions designed to keep your websites and applications online.",
    features: ["Website Hosting", "Domain Management", "SSL Certificates", "Server Management"],
  },
  {
    number: "05",
    icon: "fa-chart-line",
    title: "Analytics & Reporting",
    desc: "Turn your business data into useful insights with dashboards, reporting and analytics solutions.",
    features: ["Business Dashboards", "Power BI Integration", "Data Analytics", "Performance Reporting"],
  },
  {
    number: "06",
    icon: "fa-bullhorn",
    title: "Digital Marketing",
    desc: "Digital marketing solutions designed to help businesses increase their visibility and reach more customers.",
    features: ["Social Media Integration", "Digital Advertising", "Lead Generation", "Online Visibility"],
  },
];

/* =========================================================
   WEBSITE DEVELOPMENT PACKAGES
   ========================================================= */

const PACKAGE_FEATURE_PREVIEW_COUNT = 5;

/* =========================================================
   SERVICES COMPONENT
   ========================================================= */

const Services = () => {
  const { user } = useAuth();
  const [websitePackages, setWebsitePackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [packagesError, setPackagesError] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [expandedPackages, setExpandedPackages] = useState(() => new Set());
  const [canScrollPackagesBack, setCanScrollPackagesBack] = useState(false);
  const [canScrollPackagesForward, setCanScrollPackagesForward] = useState(true);
  const [packagePage, setPackagePage] = useState(0);
  const [packagePageCount, setPackagePageCount] = useState(1);
  const packagesViewportRef = useRef(null);

  const total = heroSlides.length;
  const websiteOnboardingTo =
    user?.Role === "admin"
      ? "/admin/website-packages"
      : user
        ? "/website-onboarding"
        : "/register";
  const websiteOnboardingState = user
    ? undefined
    : { onboardingType: "website" };

  useEffect(() => {
    websiteApi
      .getPublicPackages()
      .then((response) => {
        setWebsitePackages(
          (response.data || []).map((pkg) => ({
            id: pkg.WebsitePackageID,
            name: pkg.Name,
            description: pkg.Description || "",
            price: pkg.IsQuoteOnly
              ? "Custom Quote"
              : `R${Number(pkg.Price || 0).toLocaleString("en-ZA")}`,
            priceLabel: pkg.PriceLabel || "",
            icon: pkg.Icon || "fa-globe",
            features: Array.isArray(pkg.Features) ? pkg.Features : [],
            popular: Boolean(pkg.IsPopular),
            enterprise: Boolean(pkg.IsQuoteOnly),
          })),
        );
      })
      .catch((loadError) => {
        setPackagesError(
          loadError.response?.data?.error || "Unable to load website packages.",
        );
      })
      .finally(() => setPackagesLoading(false));
  }, []);

  /* Auto-rotate hero (pauses on hover/focus, respects reduced motion) */
  useEffect(() => {
    if (paused) return;

    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;

    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % total);
    }, SLIDE_DURATION);

    return () => clearTimeout(timer);
  }, [currentSlide, paused, total]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % total);
  const previousSlide = () => setCurrentSlide((prev) => (prev - 1 + total) % total);

  /* ---------- Packages carousel (3 visible, slides one card at a time) ---------- */

  const updatePackageCarouselControls = () => {
    const viewport = packagesViewportRef.current;
    if (!viewport) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const currentScroll = Math.min(viewport.scrollLeft, maxScroll);

    setCanScrollPackagesBack(currentScroll > 1);
    setCanScrollPackagesForward(currentScroll < maxScroll - 1);

    const track = viewport.firstElementChild;
    const firstCard = viewport.querySelector(".package-card");
    if (track && firstCard) {
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const step = firstCard.offsetWidth + gap;
      if (step > 0) {
        const visiblePackages = Math.max(
          1,
          Math.round((viewport.clientWidth + gap) / step)
        );
        const pageCount = Math.max(
          1,
          websitePackages.length - visiblePackages + 1
        );
        const page = Math.min(
          pageCount - 1,
          Math.round(currentScroll / step)
        );
        setPackagePageCount(pageCount);
        setPackagePage(page);
      }
    }
  };

  /* One step = width of a single card + the gap between cards */
  const getPackageStep = () => {
    const viewport = packagesViewportRef.current;
    if (!viewport) return 0;

    const track = viewport.firstElementChild;
    const firstCard = viewport.querySelector(".package-card");
    if (!track || !firstCard) return viewport.clientWidth;

    const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
    return firstCard.offsetWidth + gap;
  };

  const scrollPackages = (direction) => {
    const viewport = packagesViewportRef.current;
    if (!viewport) return;

    viewport.scrollBy({
      left: direction * getPackageStep(),
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  };

  const goToPackage = (index) => {
    const viewport = packagesViewportRef.current;
    if (!viewport) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const target = Math.min(maxScroll, Math.max(0, index * getPackageStep()));
    viewport.scrollTo({
      left: target,
      behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  };

  const togglePackage = (packageName) => {
    setExpandedPackages((previous) => {
      const next = new Set(previous);
      if (next.has(packageName)) {
        next.delete(packageName);
      } else {
        next.add(packageName);
      }
      return next;
    });
  };

  const activeSlide = heroSlides[currentSlide];

  useEffect(() => {
    updatePackageCarouselControls();
    window.addEventListener("resize", updatePackageCarouselControls);
    return () => window.removeEventListener("resize", updatePackageCarouselControls);
  }, [websitePackages.length]);

  return (
    <div className="services-page">
      {/* =====================================================
          HERO SLIDER
      ===================================================== */}
      <section
        className="services-hero"
        aria-roledescription="carousel"
        aria-label="Web design and development"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div className="services-hero-shape services-shape-one" aria-hidden="true" />
        <div className="services-hero-shape services-shape-two" aria-hidden="true" />
        <div className="services-hero-grid" aria-hidden="true" />

        <div className="container services-hero-container">
          {/* Left */}
          <div className="services-hero-content" key={currentSlide} aria-live="polite">
            <div className="services-label">
              <span className="services-label-dot" aria-hidden="true" />
              {activeSlide.badge}
            </div>

            <h1>{activeSlide.title}</h1>
            <p>{activeSlide.desc}</p>

            <div className="services-hero-actions">
              <Link
                to={websiteOnboardingTo}
                state={websiteOnboardingState}
                className="services-btn services-btn-primary"
              >
                Start a Project
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </Link>

              <a href="#website-packages" className="services-btn services-btn-light">
                View Packages
                <i className="fas fa-chevron-down" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Right */}
          <div className="services-hero-visual">
            <div className="services-hero-image-wrapper" key={`image-${currentSlide}`}>
              <img src={activeSlide.img} alt={activeSlide.title} className="services-hero-image" />
              <div className="services-image-overlay" />
            </div>

            <div className="services-hero-floating-card">
              <div className="services-floating-icon">
                <i className="fas fa-check" aria-hidden="true" />
              </div>
              <div>
                <strong>Professional Websites</strong>
                <span>Built for your business</span>
              </div>
            </div>
          </div>
        </div>

        {/* Slider controls */}
        <div className="services-slider-controls">
          <button type="button" className="services-slider-arrow" onClick={previousSlide} aria-label="Previous slide">
            <i className="fas fa-arrow-left" aria-hidden="true" />
          </button>

          <div className="services-slider-dots">
            {heroSlides.map((slide, index) => (
              <button
                key={slide.badge}
                type="button"
                className={`services-slider-dot ${currentSlide === index ? "active" : ""}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={currentSlide === index ? "true" : undefined}
              />
            ))}
          </div>

          <button type="button" className="services-slider-arrow" onClick={nextSlide} aria-label="Next slide">
            <i className="fas fa-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* =====================================================
          SERVICES
      ===================================================== */}
      <section id="our-services" className="services-section">
        <div className="container">
          <div className="services-section-header">
            <div className="services-section-label">Our services</div>
            <h2>
              Everything You Need to
              <span> Grow Online</span>
            </h2>
            <p>
              We combine design, development, technology and data to create
              digital solutions that solve real business problems.
            </p>
          </div>

          <div className="services-grid">
            {services.map((service) => (
              <div className="service-card" key={service.number}>
                <div className="service-card-top">
                  <div className="service-icon">
                    <i className={`fas ${service.icon}`} aria-hidden="true" />
                  </div>
                  <span className="service-number">{service.number}</span>
                </div>

                <h3>{service.title}</h3>
                <p className="service-description">{service.desc}</p>

                <ul className="service-features">
                  {service.features.map((feature) => (
                    <li key={feature}>
                      <i className="fas fa-check" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =====================================================
          WEBSITE DEVELOPMENT PACKAGES
      ===================================================== */}
      <section id="website-packages" className="packages-section">
        <div className="container">
          <div className="services-section-header">
            <div className="services-section-label">Website development</div>
            <h2>
              Choose the right plan for
              <span> your business</span>
            </h2>
            <p>
              Flexible packages designed to help you get online, grow your brand and sell more.
              Start with what you need, and scale when you're ready.
            </p>
          </div>

          {packagesError && <p role="alert">{packagesError}</p>}
          {packagesLoading && <p>Loading website packages...</p>}
          {!packagesLoading && !packagesError && websitePackages.length === 0 && (
            <p>No website packages are currently available.</p>
          )}
          <div className="packages-carousel">
            <div className="packages-carousel-stage">
              <div
                className="packages-carousel-viewport"
                ref={packagesViewportRef}
                onScroll={updatePackageCarouselControls}
                role="region"
                aria-label="Website packages"
                tabIndex={0}
              >
                <div className="packages-grid">
                  {websitePackages.map((pkg) => {
                    const isExpanded = expandedPackages.has(pkg.name);
                    const hasMoreFeatures = pkg.features.length > PACKAGE_FEATURE_PREVIEW_COUNT;
                    const visibleFeatures = isExpanded
                      ? pkg.features
                      : pkg.features.slice(0, PACKAGE_FEATURE_PREVIEW_COUNT);
                    const featureListId = `website-package-features-${pkg.name
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}`;

                    return (
                      <div
                        key={pkg.id}
                        className={`package-card ${pkg.popular ? "package-popular" : ""} ${pkg.enterprise ? "package-enterprise" : ""}`}
                      >
                        {pkg.popular && (
                          <div className="package-popular-label">
                            <i className="fas fa-star" aria-hidden="true" />
                            Most Popular
                          </div>
                        )}

                        <div className="package-card-heading">
                          <div className="package-header">
                            <div className="package-icon">
                              <i
                                className={`fas ${pkg.icon}`}
                                aria-hidden="true"
                              />
                            </div>
                            <h3>{pkg.name}</h3>
                            <p>{pkg.description}</p>
                          </div>

                          <div className="package-price">
                            <strong>{pkg.price}</strong>
                            {pkg.priceLabel && <span>/ {pkg.priceLabel}</span>}
                          </div>
                        </div>

                        <div className="package-features" id={featureListId}>
                          {visibleFeatures.map((feature) => (
                            <div className="package-feature" key={feature}>
                              <span className="package-check">
                                <i className="fas fa-check" aria-hidden="true" />
                              </span>
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>

                        {hasMoreFeatures && (
                          <button
                            type="button"
                            className="package-toggle-button"
                            onClick={() => togglePackage(pkg.name)}
                            aria-expanded={isExpanded}
                            aria-controls={featureListId}
                          >
                            {isExpanded ? "Show less" : `Show ${pkg.features.length - PACKAGE_FEATURE_PREVIEW_COUNT} more`}
                            <i className={`fas ${isExpanded ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true" />
                          </button>
                        )}

                        <Link
                          to={
                            user?.Role === "admin"
                              ? "/admin/website-packages"
                              : user
                                ? `/website-onboarding?packageId=${encodeURIComponent(pkg.id)}`
                                : "/register"
                          }
                          state={
                            user
                              ? undefined
                              : {
                                  onboardingType: "website",
                                  websitePackageId: pkg.id,
                                }
                          }
                          className={`package-button ${pkg.popular ? "package-button-primary" : ""}`}
                        >
                          Get Started
                          <i className="fas fa-arrow-right" aria-hidden="true" />
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Centered Side Back / Next buttons */}
              <div className="packages-carousel-controls">
                <button
                  type="button"
                  className="packages-carousel-button"
                  onClick={() => scrollPackages(-1)}
                  disabled={!canScrollPackagesBack}
                  aria-label="Show previous website package"
                >
                  <i className="fas fa-chevron-left" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="packages-carousel-button"
                  onClick={() => scrollPackages(1)}
                  disabled={!canScrollPackagesForward}
                  aria-label="Show next website package"
                >
                  <i className="fas fa-chevron-right" aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Bottom Dots (Mobile Only) */}
            <div className="packages-carousel-dots" aria-label="Package carousel position">
              {Array.from({ length: packagePageCount }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`packages-carousel-dot ${packagePage === index ? "active" : ""}`}
                  onClick={() => goToPackage(index)}
                  aria-label={`Go to package slide ${index + 1}`}
                  aria-current={packagePage === index ? "true" : undefined}
                />
              ))}
            </div>
          </div>

          <div className="packages-note">
            <i className="fas fa-info-circle" aria-hidden="true" />
            <p>
              All website packages are once-off development packages. Additional
              services, ongoing maintenance, advertising, advanced integrations
              or custom functionality can be quoted separately where required.
            </p>
          </div>
        </div>
      </section>

      {/* =====================================================
          WHY TOORA FLEX
      ===================================================== */}
      <section className="services-benefits">
        <div className="container">
          <div className="benefits-wrapper">
            <div className="benefits-content">
              <div className="services-section-label">Why Toora Flex</div>
              <h2>
                Technology That Works
                <span> for Your Business</span>
              </h2>
              <p>
                We don't believe in one-size-fits-all digital solutions. We take
                the time to understand your business and build technology around
                what you actually need.
              </p>
              <Link to="/contact" className="services-text-link">
                Talk to our team
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>

            <div className="benefits-list">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-bullseye" aria-hidden="true" />
                </div>
                <div>
                  <h3>Business Focused</h3>
                  <p>Technology designed around your business objectives, not unnecessary features.</p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon teal">
                  <i className="fas fa-expand-arrows-alt" aria-hidden="true" />
                </div>
                <div>
                  <h3>Scalable Solutions</h3>
                  <p>Start with what you need today and expand as your business grows.</p>
                </div>
              </div>

              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-headset" aria-hidden="true" />
                </div>
                <div>
                  <h3>Ongoing Support</h3>
                  <p>We remain available to maintain, improve and support your digital systems.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CTA
      ===================================================== */}
      <section className="services-cta">
        <div className="container">
          <div className="services-cta-wrapper">
            <div className="services-cta-content">
              <div className="services-cta-label">Let's work together</div>
              <h2>
                Have a Project
                <span> in Mind?</span>
              </h2>
              <p>
                Tell us what you need and we'll help you find the right digital
                solution for your business.
              </p>
              <Link
                to={websiteOnboardingTo}
                state={websiteOnboardingState}
                className="services-btn services-btn-cta"
              >
                Get a Free Consultation
                <i className="fas fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>
            <div className="services-cta-mark" aria-hidden="true">TF</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;